
import React, { useState, useContext, useMemo, useCallback } from 'react';
import { Page, Puzzle } from '../types';
import { PageContext } from '../PageContext';
import { usePuzzles } from '../PuzzleContext';
import { useAuth } from '../AuthContext';
import PostCard from '../components/PostCard';
import { forYouCardsData } from '../mockData';
import ForYouCard from '../components/ForYouCard';

const quotes = [
    "Got a good one? Stump your friends!",
    "Create a masterpiece, perplex the masses.",
    "Share a mystery, challenge the world.",
    "What's in the picture? Start a new puzzle.",
    "Unleash your creativity, post a clue!"
];

const HomePage: React.FC = () => {
    const pageContext = useContext(PageContext);
    const { puzzles, reportPuzzle, puzzlesLoading } = usePuzzles();
    const { userProfile, allUsers, usersLoading } = useAuth();
    const [quoteIndex, setQuoteIndex] = useState(0);

    const forYouFeed = forYouCardsData;

    const cleanPuzzles = useMemo(() => {
        // Allow rendering if we have puzzles, even if user is not logged in (Guest Mode)
        if (puzzlesLoading) {
            return [];
        }
        
        return puzzles.filter(p => {
            // Hide self-created posts that are marked not to show in the main feed
            if (userProfile && p.creatorId === userProfile.uid && p.showInMainFeed === false) {
                return false;
            }
    
            // Find the creator in the user list (if available)
            // Note: In guest mode, allUsers might be empty or limited depending on security rules, 
            // but the puzzle object contains basic creatorProfile info.
            const creator = allUsers.find(u => u.uid === p.creatorId);
            
            // If the creator's profile doesn't exist in our list AND we are logged in (meaning we should see them), 
            // we might hide it. But for Guests, we trust the puzzle data.
            
            // Block Check (Only applies if logged in)
            if (userProfile) {
                const iHaveBlockedThem = userProfile.blockedUsers?.includes(p.creatorId);
                const theyHaveBlockedMe = creator?.blockedUsers?.includes(userProfile.uid);
                
                if (iHaveBlockedThem || theyHaveBlockedMe) {
                    return false;
                }
            }
            
            return true;
        }).sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());
    }, [puzzles, userProfile, allUsers, puzzlesLoading]);


    const feedWithAds = useMemo(() => {
        return cleanPuzzles;
    }, [cleanPuzzles]);


    const openCreatePage = () => {
        if (!userProfile) {
            pageContext?.switchPage(Page.Login, 'Sign In');
            return;
        }
        pageContext?.switchPage(Page.CreatePost, 'Create Post');
    };
    
    const openEditPage = useCallback((puzzle: Puzzle) => {
        pageContext?.switchPage(Page.CreatePost, 'Edit Post', { puzzleToEdit: puzzle });
    }, [pageContext]);

    const handleForYouClick = (page: Page, title: string) => {
        if (!userProfile && (page !== Page.Search && page !== Page.Leaderboard)) {
             pageContext?.switchPage(Page.Login, 'Sign In');
             return;
        }
        pageContext?.switchPage(page, title);
    };

    const isLoading = puzzlesLoading;

    return (
        <div className="space-y-4 pb-16">
            
            {/* For You Section */}
            <div>
                <h2 className="text-xl font.bold text-gray-800 mb-2 px-1">For You</h2>
                <div className="flex space-x-4 overflow-x-auto pb-2 -mx-4 px-4 custom-scrollbar">
                     {forYouFeed.map((card, index) => (
                        <React.Fragment key={card.id}>
                            <ForYouCard
                                {...card}
                                onClick={() => handleForYouClick(card.page, card.pageTitle)}
                            />
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* Create a Post Banner */}
            <div
                id="tour-create-post"
                onClick={openCreatePage}
                className="bg-[var(--primary-dark-purple)] p-3 rounded-lg shadow-md cursor-pointer hover:bg-purple-900 transition-colors"
            >
                <div className="flex items-center space-x-3">
                    <div className="flex-grow bg-white rounded-lg px-4 py-2 text-center flex items-center justify-center min-h-[40px]">
                        <p className="text-3xl font.bold aurora-text" style={{ fontFamily: "'Caveat', cursive" }}>
                            {quotes[quoteIndex]}
                        </p>
                    </div>
                    <i className="ph-duotone ph-image text-2xl text-[var(--accent-pink)]"></i>
                </div>
            </div>
            
             {/* Feed Section */}
            <div id="tour-feed">
                <h2 className="text-xl font.bold text-gray-800 mb-2 px-1">Feed</h2>
                <div className="space-y-4">
                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <div className="spinner-white" style={{ borderColor: 'rgba(107, 33, 168, 0.3)', borderLeftColor: 'var(--primary-dark-purple)' }}></div>
                        </div>
                    ) : feedWithAds.length > 0 ? (
                        feedWithAds.map((item) => {
                            return <PostCard key={item.id} puzzle={item as Puzzle} onEdit={openEditPage} onReport={reportPuzzle} />;
                        })
                    ) : (
                         <p className="text-center text-gray-500 py-12">No puzzles on your feed yet.</p>
                    )}
                </div>
            </div>

        </div>
    );
};

export default HomePage;
